import { useEffect, useMemo, useState } from 'react';
import { useQuery, NetworkStatus } from '@apollo/client';
import type { DocumentNode } from 'graphql';

/** Minimal Relay-connection shape shared by the list queries. */
export interface RelayConnection<TNode> {
  count: number;
  edges?: ReadonlyArray<{ node: TNode } | null> | null;
  pageInfo: {
    startCursor?: string | null;
    endCursor?: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export interface UseListViewOptions<TData, TNode, TFilters> {
  /** The list connection query (TData is supplied via the generic at the call site). */
  query: DocumentNode;
  /** Pull the connection out of the query result. */
  selectConnection: (_data: TData | undefined) => RelayConnection<TNode> | undefined;
  /** Map the current search / cursor / filters into the query's variables. */
  buildVariables: (_args: {
    first: number;
    filter: string;
    after?: string;
    before?: string;
    filters: TFilters;
  }) => Record<string, unknown>;
  /** Initial filter state (e.g. selected statuses). */
  initialFilters: TFilters;
  pageSize?: number;
  searchDebounceMs?: number;
}

export interface ListViewResult<TNode, TFilters> {
  items: TNode[];
  count: number;
  page: number;
  pageSize: number;
  search: string;
  setSearch: (_value: string) => void;
  filters: TFilters;
  setFilters: (_filters: TFilters) => void;
  loading: boolean; // first load, nothing to show yet
  refetching: boolean; // a search / filter / page change while data is on screen
  error: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  nextPage: () => void;
  prevPage: () => void;
  rangeLabel: string;
  retry: () => void;
}

/**
 * Owns the state + Apollo wiring for a paginated, searchable, filterable list.
 * A single data path (Apollo's `data`) — no manual copy into local state — and
 * every search/filter change resets the cursor synchronously via its own
 * setters, so cursor + page never drift out of sync with the active filter.
 */
export function useListView<TData, TNode, TFilters>({
  query,
  selectConnection,
  buildVariables,
  initialFilters,
  pageSize = 5,
  searchDebounceMs = 300,
}: UseListViewOptions<TData, TNode, TFilters>): ListViewResult<TNode, TFilters> {
  const [search, setSearchState] = useState('');
  const [filters, setFiltersState] = useState<TFilters>(initialFilters);
  const [cursor, setCursor] = useState<{ after?: string; before?: string }>({});
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(search, searchDebounceMs);

  // Any search or filter change goes through these setters, which reset
  // pagination back to the first page in the same tick as the state change.
  const setSearch = (value: string) => {
    setSearchState(value);
    setCursor({});
    setPage(1);
  };
  const setFilters = (next: TFilters) => {
    setFiltersState(next);
    setCursor({});
    setPage(1);
  };

  const variables = useMemo(() => {
    const raw = buildVariables({ first: pageSize, filter: debouncedSearch, ...cursor, filters });
    // Drop undefined keys so the sent variables are minimal and deterministic
    // (matches the previous behaviour and keeps test mocks stable).
    return Object.fromEntries(Object.entries(raw).filter(([, value]) => value !== undefined));
  }, [buildVariables, pageSize, debouncedSearch, cursor, filters]);

  const { data, previousData, error, networkStatus, refetch } = useQuery<TData>(query, {
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Fall back to the previous result so the table doesn't flash empty while a
  // page/search/filter change is in flight.
  const connection = selectConnection(data) ?? selectConnection(previousData);
  const items = (connection?.edges ?? []).flatMap((edge) => (edge ? [edge.node] : []));
  const count = connection?.count ?? 0;
  const pageInfo = connection?.pageInfo;

  const loading = networkStatus === NetworkStatus.loading && !connection;
  const refetching =
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch ||
    (networkStatus === NetworkStatus.loading && !!connection);

  const nextPage = () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    setCursor({ after: pageInfo.endCursor });
    setPage((prev) => prev + 1);
  };
  const prevPage = () => {
    if (!pageInfo?.hasPreviousPage || !pageInfo.startCursor) return;
    setCursor({ before: pageInfo.startCursor });
    setPage((prev) => Math.max(1, prev - 1));
  };

  const start = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = (page - 1) * pageSize + items.length;
  const rangeLabel = count === 0 ? '' : `${start}–${end} of ${count}`;

  return {
    items,
    count,
    page,
    pageSize,
    search,
    setSearch,
    filters,
    setFilters,
    loading,
    refetching,
    error: Boolean(error),
    hasNext: Boolean(pageInfo?.hasNextPage),
    hasPrevious: Boolean(pageInfo?.hasPreviousPage),
    nextPage,
    prevPage,
    rangeLabel,
    // A failed list is worth one more try before someone reloads the page.
    retry: () => {
      void refetch();
    },
  };
}
